import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appService } from "../../App/app.service";
import {
  getDefaultSalesType,
  getDefaultSelectedStage,
  getDefaultACV,
} from "../../util/customFunctions";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";

/**
 * Helper function to get first N values from computedFilters for a given filter type.
 * Excludes "All" if it's the first value, and sorts chronologically for date fields.
 *
 * @param {Array} computedFilters - Array of filter objects from API
 * @param {string} filterType - The type of filter to extract (e.g., "Open_Initial_Year_Month")
 * @param {number} count - Number of values to return
 * @returns {Array} Array of first N values
 */
function getFirstNValues(computedFilters, filterType, count) {
  if (!Array.isArray(computedFilters) || !filterType || count <= 0) {
    return [];
  }

  // Extract all values for this filter type
  const filterItems = computedFilters.filter((f) => f.type === filterType);
  if (filterItems.length === 0) {
    return [];
  }

  // Get the actual values - the value is stored in a property with the same name as the type
  // Also handle cases where the value might be in a different property format
  let values = filterItems
    .map((item) => {
      // Try the direct property first
      if (item[filterType] !== undefined && item[filterType] !== null && item[filterType] !== "") {
        return item[filterType];
      }
      // Fallback: check if there's a value property
      if (item.value !== undefined) {
        return item.value;
      }
      return null;
    })
    .filter((v) => v !== null && v !== undefined && v !== "");

  // Sort chronologically if it looks like a date field (contains year-month pattern)
  if (filterType.includes("Year_Month") || filterType.includes("Month")) {
    values.sort((a, b) => {
      // Handle "YYYY-MM" format - sort descending (newest first)
      if (a.match(/^\d{4}-\d{2}$/) && b.match(/^\d{4}-\d{2}$/)) {
        return b.localeCompare(a); // Reverse order for newest first
      }
      return String(b).localeCompare(String(a)); // Reverse order
    });
  } else {
    // For non-date fields, sort alphabetically
    values.sort((a, b) => String(a).localeCompare(String(b)));
  }

  // Skip "All" if it's the first value
  if (values[0] === "All" || values[0] === "all") {
    values = values.slice(1);
  }

  // Return first N values (already sorted newest first for dates)
  return values.slice(0, count);
}

/**
 * Determine the string property for a filter based on computedFilters.
 * Checks if any filter item of this type has string === "Y"
 *
 * @param {Array} computedFilters - Available filters from API
 * @param {string} filterName - Name of the filter
 * @returns {boolean} true if string should be true, false otherwise
 */
function determineStringProperty(computedFilters, filterName) {
  if (!Array.isArray(computedFilters) || !filterName) {
    return true; // Default to true
  }

  // Find a filter item of this type to check its string property
  const filterItem = computedFilters.find((f) => f.type === filterName);
  
  if (filterItem) {
    // If the filter item has string === "Y", return true
    // If it has string !== "Y" or string is falsy, return false
    return filterItem.string === "Y";
  }

  // Special cases: numeric filters always false
  const numericFilters = ["acv", "acvlt"];
  if (numericFilters.includes(filterName)) {
    return false;
  }

  // Special case: cross_sell1__c is typically false based on user example
  if (filterName === "cross_sell1__c") {
    return false;
  }

  // Default to true for most text-based filters
  return true;
}

/**
 * Process additionalFilters to handle count property:
 * - count === 0: Skip filter
 * - count > 0 AND no value: Get first N values from computedFilters
 * - value array provided: Use as-is
 *
 * @param {Array} additionalFilters - Filters with optional count property
 * @param {Array} computedFilters - Available filters from API
 * @returns {Array} Processed filters ready for API call
 */
function processAdditionalFilters(additionalFilters, computedFilters) {
  if (!Array.isArray(additionalFilters)) {
    return [];
  }

  const processed = [];

  for (const filter of additionalFilters) {
    // If count === 0, skip this filter
    if (filter.count === 0) {
      continue;
    }

    // If value array is provided, use it as-is
    if (Array.isArray(filter.value)) {
      const stringProp = filter.string !== undefined 
        ? filter.string 
        : determineStringProperty(computedFilters, filter.name);
      processed.push({
        name: filter.name,
        value: filter.value,
        string: stringProp,
      });
      continue;
    }

    // If count > 0 and no value, get first N values from computedFilters
    if (filter.count > 0 && !Array.isArray(filter.value)) {
      const values = getFirstNValues(computedFilters, filter.name, filter.count);
      if (values.length > 0) {
        const stringProp = filter.string !== undefined 
          ? filter.string 
          : determineStringProperty(computedFilters, filter.name);
        processed.push({
          name: filter.name,
          value: values,
          string: stringProp,
        });
      }
      continue;
    }

    // Default: add filter as-is (no count, no value array)
    // But ensure string property is set if not provided
    const finalFilter = { ...filter };
    if (finalFilter.string === undefined) {
      finalFilter.string = determineStringProperty(computedFilters, filter.name);
    }
    processed.push(finalFilter);
  }

  return processed;
}

/**
 * usePipelineCreatedMixData.js
 *
 * Custom hooks for fetching Pipeline Created Mix data by dimension.
 * Fetches filters, computes defaults, dispatches Redux, retrieves data, and
 * exposes normalized chart data for stacked bar cards.
 *
 * Returned interface:
 * - chartData: stacked bar friendly rows by quarter/month and categories
 * - derivedCategories: sorted categories (legend and stack order)
 * - appliedFilters: API filters applied
 * - isLoading: whether fetching is in progress
 * - error: error message if fetch fails
 *
 */
function usePipelineCreatedMixDataByDimension(dimensionKey, additionalFilters = []) {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.app.company);
  // Use local state instead of Redux for the chart data
  const [localChartData, setLocalChartData] = useState(null);

  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);
  const [isDefaultTeamLoaded, setIsDefaultTeamLoaded] = useState(false);

  const isDataLoaded = localChartData &&
    (Array.isArray(localChartData?.data)
      ? localChartData.data.length > 0
      : Array.isArray(localChartData) && localChartData.length > 0);

  /**
   * Step 1: Fetch the list of first-level (parent) team names from the team hierarchy.
   * We need this before we can make the API request with team filters.
   * Uses the same source as Team cards for consistency.
   */
  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
    fetchDefaultTeamFilter().then((teams) => {
      setDefaultTeamFilter(teams);
      setIsDefaultTeamLoaded(true);
    });
  }, []);

  useEffect(() => {
    // Don't start fetching until we know which teams to filter by
    if (!isFirstLevelTeamsLoaded || !isDefaultTeamLoaded) return;
    if (isDataLoaded || fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    setError("");
    dispatch({ type: "get_pipeline_created_mix_filters_request" });

    appService.getPipelineCreatedMixAnalysisFilters().then(
      (resp) => {
        const lkpFilters = Object.values(
          resp.message.lkpFilters.reduce((r, c) => Object.assign(r, c), {})
        );
        const defaultTimeFrame = resp.message.defaultTimeFrame;
        const computedFilters = resp.message.computedFilters;

        dispatch({
          type: "get_pipeline_created_mix_filters_success",
          json: { message: computedFilters, lkpFilters, defaultTimeFrame },
        });

        // Check if we should skip default quarter filter (check raw additionalFilters)
        const shouldSkipQuarter = additionalFilters.some(
          (f) => f.name === defaultTimeFrame && f.count === 0
        );

        // Process additionalFilters to handle count property
        const processedAdditionalFilters = processAdditionalFilters(
          additionalFilters,
          computedFilters
        );

        const stageSequence = getDefaultSelectedStage(
          computedFilters.filter((f) => f.type === "milestone_stage")
        );
        // Select only the exact stage, not all stages from that sequence onwards
        const defaultStages = computedFilters
          .filter((item) => item.sequence === stageSequence)
          .map((i) => i.milestone_stage)
          .filter(Boolean);

        const filters = [];

        // Add default timeframe filter only if not skipped
        if (!shouldSkipQuarter) {
          const tfs = getDefaultSalesType(
            computedFilters.filter((i) => i.type === defaultTimeFrame),
            defaultTimeFrame
          );
          if (tfs[0] !== "All") {
            filters.push({ 
              name: defaultTimeFrame, 
              value: tfs, 
              string: determineStringProperty(computedFilters, defaultTimeFrame)
            });
          }
        }

        if (defaultStages.length > 0) {
          filters.push({ 
            name: "milestone_stage", 
            string: true, 
            value: defaultStages 
          });
        }

        const defaultOppType = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Type"),
          "Type"
        );
        const defaultSolutionCat = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Solution_Cat"),
          "Solution_Cat"
        );
        const defaultCrossSell = getDefaultSalesType(
          computedFilters.filter((s) => s.type === "cross_sell1__c"),
          "cross_sell1__c"
        );

        // Add cross_sell1__c as default filter if not "All"
        if (defaultCrossSell[0] !== "All") {
          filters.push({ 
            name: "cross_sell1__c", 
            value: defaultCrossSell,
            string: determineStringProperty(computedFilters, "cross_sell1__c")
          });
        }

        // Add Type filter if not "All"
        if (defaultOppType[0] !== "All") {
          filters.push({ 
            name: "Type", 
            value: defaultOppType,
            string: determineStringProperty(computedFilters, "Type")
          });
        }
        if (defaultSolutionCat[0] !== "All") {
          filters.push({ 
            name: "Solution_Cat", 
            value: defaultSolutionCat,
            string: determineStringProperty(computedFilters, "Solution_Cat")
          });
        }

        // Add team filter for first-level teams to match Team cards' data subset
        // Prioritize first-level teams, fallback to default team filter if not available
        const teamsToFilter =
          Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
            ? firstLevelTeamNames
            : (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
                ? defaultTeamFilter
                : []);

        if (Array.isArray(teamsToFilter) && teamsToFilter.length > 0) {
          filters.push({
            name: "team",
            value: teamsToFilter,
            string: determineStringProperty(computedFilters, "team"),
          });
        }

        // Add default ACV filter if available
        const defaultACV = getDefaultACV(
          computedFilters.filter((f) => f.type === "acv")
        );
        if (defaultACV !== "") {
          filters.push({
            name: "acv",
            value: defaultACV,
            string: false,
          });
        }

        // Add processed additional filters
        filters.push(...processedAdditionalFilters);

        setAppliedFilters(filters);

        appService
          .getPipelineCreatedMixAnalysisData(
            computedFilters.length === 0 ? [] : filters
          )
          .then(
            (dataResp) => {
              // Save to local state instead of Redux
              setLocalChartData(dataResp.message);
              fetchingRef.current = false;
              setIsLoading(false);
            },
            (err) => {
              const msg =
                typeof err === "object" ? "Something went wrong" : err;
              setError(msg);
              fetchingRef.current = false;
              setIsLoading(false);
            }
          );
      },
      (err) => {
        const msg = typeof err === "object" ? "Something went wrong" : err;
        setError(msg);
        fetchingRef.current = false;
        setIsLoading(false);
      }
    );
  }, [
    company,
    dispatch,
    isDataLoaded,
    firstLevelTeamNames,
    isFirstLevelTeamsLoaded,
    defaultTeamFilter,
    isDefaultTeamLoaded,
    additionalFilters,
  ]);

  const { chartData, derivedCategories } = useMemo(() => {
    if (!isDataLoaded) return { chartData: [], derivedCategories: [] };
    const blocks = Array.isArray(localChartData?.data)
      ? localChartData.data
      : Array.isArray(localChartData)
      ? localChartData
      : [];

    const block = blocks.find(
      (item) =>
        item.type === dimensionKey ||
        (item.data &&
          item.data.length > 0 &&
          item.data[0][dimensionKey] !== undefined)
    );
    if (!block?.data) return { chartData: [], derivedCategories: [] };

    const rawData = block.data;
    // Pipeline Created Mix uses TimeFrame property (capital T, capital F)
    // Fallback to timeline for compatibility, then to default
    const quarterField = block.TimeFrame || block.timeline || "projected_close_fiscal_quarter";

    const totals = {};
    rawData.forEach((row) => {
      const cat = row[dimensionKey];
      if (cat) totals[cat] = (totals[cat] || 0) + (row.acv || 0);
    });

    const categories = Object.keys(totals).sort(
      (a, b) => totals[b] - totals[a]
    );

    const grouped = {};
    rawData.forEach((row) => {
      const fq = row[quarterField];
      if (!fq) return;
      if (!grouped[fq]) {
        grouped[fq] = { xAxisLabel: fq };
        categories.forEach((c) => (grouped[fq][c] = 0));
      }
      const key = row[dimensionKey];
      if (key) grouped[fq][key] = row.acv || 0;
    });

    const out = Object.values(grouped).sort((a, b) =>
      (a.xAxisLabel || "").localeCompare(b.xAxisLabel || "")
    );

    return { chartData: out, derivedCategories: categories };
  }, [localChartData, isDataLoaded, dimensionKey]);

  return { chartData, derivedCategories, appliedFilters, isLoading, error };
}

export function usePipelineCreatedMixTeamData(additionalFilters = []) {
  return usePipelineCreatedMixDataByDimension("Team", additionalFilters);
}
